import { memo, useCallback, useMemo } from 'react';
import { Button, Card, Descriptions, Modal, Tag, Typography } from 'antd';
import { useVisible } from '@wappsto/blanket';
import { useStore } from 'react-redux';
import { useTranslation } from 'translations';
import { Trans } from 'react-i18next';
import ShareTable from 'features/notifications/components/installationPrecisePermission/ShareTable';
import GenericNotification from '../GenericNotification';

const ACCEPTED = 'accepted';
const DELETED = 'deleted';

interface OldAcceptedProps {
  notificationCustomData: any;
}

// Notifications accepted using old BAREBONE frontend prompted in the wapp
const OldAccepted = memo(({ notificationCustomData }: OldAcceptedProps) => (
  <ul>
    {notificationCustomData.map((item: any) => (
      <li key={item.meta.id}>{item.meta.name_by_user || item.meta.id}</li>
    ))}
  </ul>
));

OldAccepted.displayName = 'InstallationPrecisePermission_OldAccepted';

interface NewAcceptedProps {
  notificationCustom: any;
}

// Notifications accepted using this app
const NewAccepted = memo(({ notificationCustom }: NewAcceptedProps) => {
  const { tC } = useTranslation(
    'features',
    'notifications.appNotifications.permissions.precisePermissions',
  );
  if (!notificationCustom.data) {
    return null;
  }
  return notificationCustom.data.all ? (
    <ul>
      <li>{tC('allItems', { type: notificationCustom.type })}</li>
      {notificationCustom.data.future && (
        <li>{tC('allowedFuture', { type: notificationCustom.type })}</li>
      )}
    </ul>
  ) : (
    <ul>
      {notificationCustom.data.selected.map((item: any) => (
        <li key={item.meta.id}>{item.meta.name_by_user || item.meta.id}</li>
      ))}
    </ul>
  );
});

NewAccepted.displayName = 'InstallationPrecisePermission_NewAccepted';

interface AcceptedProps {
  notification: any;
}

const Accepted = memo(({ notification }: AcceptedProps) => {
  const { tC } = useTranslation(
    'features',
    'notifications.appNotifications.permissions.precisePermissions',
  );
  if (!notification?.custom) return null;
  return (
    <Card title={tC('acceptedDescription', { type: notification.custom.type })}>
      {Array.isArray(notification.custom.data) ? (
        <OldAccepted notificationCustomData={notification.custom.data} />
      ) : (
        <NewAccepted notificationCustom={notification.custom} />
      )}
    </Card>
  );
});

Accepted.displayName = 'InstallationPrecisePermission_Accepted';

interface InstallationPrecisePermissionProps {
  notification: any;
  updateNotificationStatus: (...args: any[]) => any;
  deleteNotification: (...args: any[]) => any;
  setDropdownVisible: (...args: any[]) => any;
  onClick: (...args: any[]) => any;
  isDeleting: boolean;
}

const InstallationPrecisePermission = memo((
    {
      notification,
      updateNotificationStatus,
      deleteNotification,
      setDropdownVisible,
      onClick,
      isDeleting,
    }: InstallationPrecisePermissionProps,
  ) => {
    const { t, tC } = useTranslation('features', 'notifications.appNotifications.permissions');
    const store = useStore();
    const [visible, show, hide] = useVisible(false);
    const title = notification.base?.from_name;
    const refreshName = `${title}_${notification.meta.id}_refresh`;
    const restriction = useMemo(() => {
      const method = {};
      notification.custom.method.forEach((m) => {
        method[m] = true;
      });
      return { method };
    }, [notification.custom.method]);

    const onSuccess = useCallback(
      (result) => {
        const data = { ...result, selected: [] };
        if (result.all) {
          data.selected = result.selected;
        } else {
          const state: any = store.getState();
          const typeKey = notification.custom.type;
          result.selected.forEach((id: any) => {
            let item = state.entities[typeKey][id];
            if (!item) {
              item = { meta: { id } };
            }
            data.selected.push(item);
          });
        }
        updateNotificationStatus(notification, ACCEPTED, data);
        hide();
      },
      [hide, notification, store, updateNotificationStatus],
    );

    const onError = useCallback(
      (error) => {
        if (error.status === 404 || error.status === 401) {
          updateNotificationStatus(notification, DELETED);
        }
      },
      [updateNotificationStatus, notification],
    );

    const showPopup = useCallback(() => {
      setDropdownVisible(false);
      show();
    }, [setDropdownVisible, show]);

    const query = useMemo(() => {
      let strQuery = '';
      Object.keys(notification.custom.limitation).forEach((strKey) => {
        let level = '';
        const key = parseInt(strKey, 10);
        if (key === 0) {
          level = 'this_';
        } else if (key < 0) {
          level = 'child_';
          if (key < -1) {
            level += `${key * -1}_`;
          }
        } else {
          level = 'parent_';
          if (key > 1) {
            level += `${key}_`;
          }
        }
        Object.keys(notification.custom.limitation[key]).forEach((operation) => {
          const operationData = notification.custom.limitation[key][operation];
          if (operationData.constructor === Object) {
            Object.keys(operationData).forEach((attribute) => {
              const filterArray = operationData[attribute];
              strQuery += level + attribute + operation;
              if (filterArray.length === 1) {
                strQuery += `${filterArray[0]}&`;
              } else {
                strQuery += `[${filterArray.toString()}]&`;
              }
            });
          } else if (operationData.constructor === Array) {
            strQuery += `${level}${operation}=`;
            if (operationData.length === 1) {
              strQuery += `${operationData[0]}&`;
            } else {
              strQuery += `[${operationData.toString()}]&`;
            }
          }
        });
      });
      return strQuery.slice(0, -1);
    }, [notification.custom.limitation]);

    const showRestrictions = () => {
      const r = [];
      if (restriction.method) {
        Object.keys(restriction.method).forEach((method) => {
          if (restriction.method[method]) {
            r.push(
              <Tag color="blue" key={method}>
                {t(`accessMethods.${method}`)}
              </Tag>,
            );
          }
        });
      }
      return r;
    };
    const items = [
      {
        key: 'requirements',
        label: (
          <Trans
            t={t}
            i18nKey="precisePermissions.minimumItemsRequiredToSelect"
            values={{ number: notification.custom.quantity, type: notification.custom.type }}
          />
        ),
        children: <Typography.Text strong>{notification.custom.quantity}</Typography.Text>,
      },
      {
        key: 'access',
        label: tC('precisePermissions.access'),
        children: showRestrictions(),
      },
      {
        key: 'message',
        label: tC('precisePermissions.messageFromApp'),
        children: <Typography.Text strong>{notification.custom.message}</Typography.Text>,
      },
    ];

    return (
      <>
        <Modal
          className="modal large"
          title={t(
            `precisePermissions.${
              notification.read === ACCEPTED ? 'acceptedTitle' : 'requestTitle'
            }`,
            { name: notification.base.from_name },
          )}
          open={visible}
          onCancel={hide}
          footer={null}
          destroyOnHidden>
          <Descriptions
            size="small"
            column={2}
            items={items}
            bordered
            style={{ marginBottom: 30 }}
          />

          {notification.read === ACCEPTED ? (
            <Accepted notification={notification} />
          ) : (
            <ShareTable
              itemId={notification.base.from}
              type={notification.custom.type}
              query={query}
              onError={onError}
              onSuccess={onSuccess}
              minSelected={notification.custom.quantity}
              restriction={restriction}
              refreshName={refreshName}
            />
          )}
        </Modal>
        <GenericNotification
          title={null}
          description={t(
            `precisePermissions.${
              notification.read === ACCEPTED ? 'acceptedTitle' : 'requestTitle'
            }`,
            {
              type: notification.custom.type,
              name: notification.base.from_name,
            },
          )}
          notification={notification}
          deleteNotification={deleteNotification}
          isDeleting={isDeleting}
          onClick={onClick}
          extra={
            notification.read !== DELETED && (
              <Button type="primary" size="small" onClick={showPopup}>
                {tC('precisePermissions.viewRequestButton')}
              </Button>
            )
          }
        />
      </>
    );
  },
);

InstallationPrecisePermission.displayName = 'InstallationPrecisePermission';

export default InstallationPrecisePermission;
